/**
 * Shared ECharts configuration.
 *
 * Centralised so every chart in the app shares one grid geometry, one tooltip
 * behaviour and one set of ink tokens. A per-chart `option` object is the usual
 * way visual consistency rots.
 */

import type { EChartsOption } from "echarts";
import { CHROME } from "./palette";
import { currentTheme } from "./theme";

/**
 * An object whose every property is read at the moment it is used.
 *
 * These four were plain constants, which meant the colours in them were read
 * ONCE - when this module first loaded - and frozen for the session. Switching
 * theme then changed every chart's series and background and left its axes,
 * gridlines and tooltips in the old theme: dark gridlines on a white card,
 * a black tooltip box. Getters fix that without touching the 28 places that
 * use them, because both ways they are used - spread into an option, or
 * handed to ECharts by reference - read the properties when the chart is
 * drawn, and a chart is redrawn when the theme changes.
 */
function live<S extends Record<string, () => unknown>>(
  spec: S,
): { readonly [K in keyof S]: ReturnType<S[K]> } {
  const out = {} as { [K in keyof S]: ReturnType<S[K]> };
  for (const key of Object.keys(spec) as (keyof S)[]) {
    Object.defineProperty(out, key, { get: spec[key], enumerable: true });
  }
  return out;
}

export const AXIS_LABEL = live({
  color: () => CHROME.mutedInk,
  fontSize: () => 11,
  fontFamily: () => "Inter, system-ui, sans-serif",
});

/** Recessive grid: present enough to read a value, quiet enough to ignore. */
export const SPLIT_LINE = live({
  show: () => true,
  lineStyle: () => ({ color: CHROME.gridline, width: 1, type: "solid" as const }),
});

export const AXIS_LINE = live({
  show: () => true,
  lineStyle: () => ({ color: CHROME.axis, width: 1 }),
});

export const TOOLTIP_BASE = live({
  backgroundColor: () => CHROME.tooltip,
  borderColor: () => CHROME.axis,
  borderWidth: () => 1,
  padding: () => [8, 12] as [number, number],
  textStyle: () => ({ color: CHROME.primaryInk, fontSize: 12, fontFamily: "Inter, sans-serif" }),
  // A half-black shadow lifts a box off near-black and smears one on white.
  extraCssText: () =>
    currentTheme() === "light"
      ? "box-shadow: 0 6px 18px rgba(20,24,38,.14); border-radius: 8px;"
      : "box-shadow: 0 8px 24px rgba(0,0,0,.5); border-radius: 8px;",
});

/**
 * Base option every chart extends.
 *
 * `animation: false` is deliberate. These charts routinely carry tens of
 * thousands of points and refetch on every zoom; animating that costs frames
 * and gains nothing, because the reader is comparing values, not watching a
 * transition.
 */
export function baseOption(): EChartsOption {
  return {
    animation: false,
    backgroundColor: "transparent",
    textStyle: { fontFamily: "Inter, system-ui, sans-serif" },
    grid: {
      left: 64,
      right: 24,
      top: 28,
      bottom: 52,
      containLabel: false,
    },
    tooltip: {
      ...TOOLTIP_BASE,
      trigger: "axis",
      axisPointer: {
        type: "cross",
        animation: false,
        lineStyle: { color: CHROME.secondaryInk, width: 1, type: "dashed" },
        crossStyle: { color: CHROME.secondaryInk, width: 1, type: "dashed" },
        label: { backgroundColor: CHROME.gridline, color: CHROME.primaryInk, fontSize: 11 },
      },
    },
  };
}

export function timeAxis(name?: string) {
  return {
    type: "time" as const,
    name,
    nameLocation: "middle" as const,
    nameGap: 32,
    nameTextStyle: { color: CHROME.secondaryInk, fontSize: 11 },
    axisLabel: { ...AXIS_LABEL, hideOverlap: true },
    axisLine: AXIS_LINE,
    axisTick: { show: false },
    splitLine: { ...SPLIT_LINE, lineStyle: { ...SPLIT_LINE.lineStyle, opacity: 0.5 } },
  };
}

export function valueAxis(name?: string, opts: { position?: "left" | "right" } = {}) {
  return {
    type: "value" as const,
    name,
    nameLocation: "middle" as const,
    nameGap: 46,
    nameTextStyle: { color: CHROME.secondaryInk, fontSize: 11 },
    position: opts.position,
    axisLabel: { ...AXIS_LABEL, hideOverlap: true },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: SPLIT_LINE,
    scale: true,
  };
}

/**
 * Drag-to-zoom on the x axis.
 *
 * `filterMode: "none"` matters: the default drops out-of-window points, which
 * makes the y axis rescale as you pan and destroys the visual comparison the
 * reader is making.
 */
export function xZoom(onRange?: boolean) {
  return [
    { type: "inside" as const, xAxisIndex: 0, filterMode: "none" as const, zoomOnMouseWheel: "shift" as const },
    ...(onRange
      ? [
          {
            type: "slider" as const,
            xAxisIndex: 0,
            filterMode: "none" as const,
            height: 22,
            bottom: 8,
            borderColor: CHROME.axis,
            backgroundColor: CHROME.deep,
            fillerColor: "rgba(57,135,229,0.14)",
            handleStyle: { color: "#3987e5", borderColor: "#3987e5" },
            moveHandleStyle: { color: CHROME.axis },
            dataBackground: {
              lineStyle: { color: CHROME.axis },
              areaStyle: { color: CHROME.raised },
            },
            textStyle: { color: CHROME.mutedInk, fontSize: 10 },
          },
        ]
      : []),
  ];
}

/** Line marks: 2px, no point markers until the series is sparse enough to need them. */
export function lineStyleFor(color: string, pointCount: number) {
  return {
    lineStyle: { width: 2, color },
    itemStyle: { color },
    showSymbol: pointCount <= 60,
    symbolSize: 8,
    symbol: "circle" as const,
    sampling: "lttb" as const,
    smooth: false,
    // Canvas + progressive rendering is what holds 60fps at 100k points; the
    // SVG renderer stalls an order of magnitude earlier.
    large: pointCount > 2000,
    largeThreshold: 2000,
    progressive: 4000,
    progressiveThreshold: 8000,
    emphasis: { focus: "series" as const, lineStyle: { width: 3 } },
  };
}

export function formatValue(value: number | null | undefined, precision = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude < 0.001 || magnitude >= 1e6)) {
    return value.toExponential(2);
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

export function compactNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 })
    .format(value);
}
